import { VonModelType, VonModelConfig } from './types';

// Model Configuration Constants
export const VON_MODEL_CONFIGS: Record<VonModelType, VonModelConfig> = {
  A1: {
    id: 'A1',
    name: 'Llama Lightning',
    tier: 'Fast',
    description: 'Ultra-fast responses for quick questions',
    contextLength: '32K tokens',
    costTier: '$',
    bestFor: ['Quick questions', 'Simple tasks', 'Instant responses']
  },
  A2: {
    id: 'A2',
    name: 'Llama Compact',
    tier: 'Fast',
    description: 'Efficient model for general conversation',
    contextLength: '32K tokens',
    costTier: '$',
    bestFor: ['General chat', 'Quick analysis', 'Follow-up questions']
  },
  A3: {
    id: 'A3',
    name: 'Llama Mini',
    tier: 'Fast',
    description: 'Fastest responses for basic interactions',
    contextLength: '32K tokens',
    costTier: '$',
    bestFor: ['Basic chat', 'Simple questions', 'Quick confirmations']
  },
  A4: {
    id: 'A4',
    name: 'Gemma Smart',
    tier: 'Fast',
    description: 'Google\'s efficient model for balanced performance',
    contextLength: '32K tokens',
    costTier: '$',
    bestFor: ['Balanced responses', 'General analysis', 'Smart chat']
  },
  A5: {
    id: 'A5',
    name: 'Hermes Pro',
    tier: 'Fast',
    description: 'Advanced fast model with enhanced reasoning',
    contextLength: '32K tokens',
    costTier: '$',
    bestFor: ['Enhanced reasoning', 'Complex chat', 'Detailed responses']
  },
  A6: {
    id: 'A6',
    name: 'Mistral Swift',
    tier: 'Fast',
    description: 'Instruction-tuned for precise task completion',
    contextLength: '32K tokens',
    costTier: '$',
    bestFor: ['Instruction following', 'Task completion', 'Precise responses']
  },
  A7: {
    id: 'A7',
    name: 'Qwen Smart',
    tier: 'Fast',
    description: 'Advanced reasoning with multilingual support',
    contextLength: '32K tokens',
    costTier: '$',
    bestFor: ['General reasoning', 'Multilingual chat', 'Smart responses']
  },
  A8: {
    id: 'A8',
    name: 'Arcee Blitz',
    tier: 'Fast',
    description: 'Ultra-efficient processing and summarization',
    contextLength: '32K tokens',
    costTier: '$',
    bestFor: ['Quick processing', 'Text generation', 'Efficient responses']
  },
  L1: {
    id: 'L1',
    name: 'Deep Thinker',
    tier: 'Premium',
    description: 'Complex analysis and reasoning tasks',
    contextLength: '32K tokens',
    costTier: '$$',
    bestFor: ['Complex analysis', 'Detailed reasoning', 'Strategic insights']
  },
  L4: {
    id: 'L4',
    name: 'Qwen Powerhouse',
    tier: 'Premium',
    description: 'Advanced reasoning with coding expertise',
    contextLength: '32K tokens',
    costTier: '$$',
    bestFor: ['Technical analysis', 'Reasoning tasks', 'Code assistance']
  },

  L5: {
    id: 'L5',
    name: 'Virtuoso Balance',
    tier: 'Premium',
    description: 'Efficient balanced performance for all tasks',
    contextLength: '32K tokens',
    costTier: '$$',
    bestFor: ['Balanced responses', 'Content processing', 'Analysis']
  },
  L6: {
    id: 'L6',
    name: 'Mixtral Titan',
    tier: 'Premium',
    description: 'Large mixture-of-experts for complex reasoning',
    contextLength: '32K tokens',
    costTier: '$$',
    bestFor: ['Complex reasoning', 'Advanced analysis', 'Multilingual tasks']
  },
  Z1: {
    id: 'Z1',
    name: 'Virtuoso Ultra',
    tier: 'Research',
    description: 'Research-grade comprehensive analysis',
    contextLength: '32K tokens',
    costTier: '$$$',
    bestFor: ['Research analysis', 'Deep insights', 'Comprehensive reasoning']
  },

  Z3: {
    id: 'Z3',
    name: 'Llama Flagship',
    tier: 'Research',
    description: 'Meta\'s flagship model for comprehensive analysis',
    contextLength: '32K tokens',
    costTier: '$$$',
    bestFor: ['Research analysis', 'Comprehensive analysis', 'Complex reasoning']
  },
  Z4: {
    id: 'Z4',
    name: 'DeepSeek Vision',
    tier: 'Research',
    description: 'Next-gen reasoning and pattern detection',
    contextLength: '32K tokens',
    costTier: '$$$',
    bestFor: ['Pattern recognition', 'Complex research', 'Advanced insights']
  },

};

// Helper functions for model selection
export const getModelsByTier = (tier: 'Fast' | 'Premium' | 'Research'): VonModelConfig[] => {
  return Object.values(VON_MODEL_CONFIGS).filter(model => model.tier === tier);
};

export const getModelConfig = (modelType: VonModelType): VonModelConfig => {
  return VON_MODEL_CONFIGS[modelType];
};

// Model name mapping for backend (internal use only)
const MODEL_MAPPING: Record<VonModelType, string> = {
  A1: 'llama3-8b',
  A2: 'llama3.2-3b', 
  A3: 'llama3.2-1b',
  A4: 'gemma2-9b',
  A5: 'Nous-Hermes-2-Mixtral-8x7B-DPO',
  A6: 'mistral-7b-instruct',
  A7: 'Qwen2.5-7B',
  A8: 'arcee-blitz',
  L1: 'gemma3-27b',
  L4: 'Qwen2.5-32B',
  L5: 'virtuoso-medium-v2-premium',
  L6: 'mixtral-8x22b-instruct',
  Z1: 'virtuoso-large',
  Z3: 'llama3-70b',
  Z4: 'deepseek-v3'
};

export const getActualModelName = (modelType: VonModelType): string => {
  return MODEL_MAPPING[modelType];
};

// Auto-selection logic based on query complexity
export const selectOptimalModel = (query: string, hasFilters: boolean = false): VonModelType => {
  const queryLength = query.length;
  const complexityIndicators = [
    'analyze', 'compare', 'pattern', 'trend', 'insight', 'relationship',
    'comprehensive', 'detailed', 'research', 'across', 'between'
  ];
  
  const hasComplexityIndicators = complexityIndicators.some(indicator => 
    query.toLowerCase().includes(indicator)
  );
  
  // For very complex queries with filters - use Premium tier
  if (hasComplexityIndicators && hasFilters && queryLength > 200) {
    return 'L1'; // Deep analysis with premium model
  }
  
  // For complex queries or filtered queries - use advanced Fast tier
  if (hasComplexityIndicators || hasFilters || queryLength > 150) {
    return 'A5'; // Hermes Pro for enhanced reasoning
  }
  
  // For medium complexity - use balanced models
  if (queryLength > 100) {
    return 'A4'; // Gemma Smart for balanced performance
  }
  
  // For quick questions - use fastest models
  if (queryLength < 30) {
    return 'A3'; // Llama Mini for instant responses
  }
  
  // For general conversation - use efficient model
  if (queryLength < 80) {
    return 'A2'; // Llama Compact for general chat
  }
  
  // Default to Llama Lightning for standard queries
  return 'A1';
}; 